import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { scrollToSection, fireConfetti } from "../lib/site";

const LINKS = [
  { label: "Experience", href: "#experience" },
  { label: "Packages", href: "#packages" },
  { label: "Add-Ons", href: "#addons" },
  { label: "Gallery", href: "#gallery" },
  { label: "About", href: "/about" },
  { label: "FAQ", href: "/faq" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [flash, setFlash] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    let alive = true;
    let t;
    const schedule = () => {
      t = setTimeout(() => {
        if (!alive) return;
        setFlash((f) => f + 1);
        schedule();
      }, 2200 + Math.random() * 4800);
    };
    schedule();
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, []);

  const go = (href) => {
    setOpen(false);
    if (href.startsWith("/")) {
      navigate(href);
    } else if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: href } });
    } else {
      scrollToSection(href);
    }
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.4 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-white/75 border-b border-black/5 shadow-[0_4px_24px_rgba(145,0,141,0.06)]"
          : "bg-transparent"
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-28 md:h-36 flex items-center justify-between">
        <button
          onClick={() => go("#hero")}
          data-testid="nav-logo"
          className="flex items-center"
        >
          <span className="relative inline-block">
            <img
              src="/logo.png"
              alt="GlamBooth Cayman"
              className={`w-auto object-contain py-1 transition-all duration-500 ${
                scrolled
                  ? "h-[76px] md:h-[104px]"
                  : "h-[96px] md:h-[140px] drop-shadow-[0_0_10px_rgba(255,255,255,0.65)] brightness-110"
              }`}
            />
            {flash > 0 && <span key={flash} className="logo-flash" data-testid="logo-flash" />}
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]/g, "")}`}
              className={`text-sm font-medium tracking-wide transition-colors ${
                scrolled ? "text-[#121212]/70 hover:text-[#91008d]" : "text-white/80 hover:text-white"
              }`}
            >
              {l.label}
            </button>
          ))}
          <button
            onClick={(e) => {
              fireConfetti(e);
              go("#contact");
            }}
            data-testid="nav-book-date-btn"
            className="btn-gold rounded-full px-6 py-2.5 text-sm font-bold tracking-wide"
          >
            Book Your Date
          </button>
        </nav>

        <button
          className={`md:hidden ${scrolled ? "text-[#91008d]" : "text-white"}`}
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden backdrop-blur-xl bg-white/90 border-b border-black/5 overflow-hidden"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  onClick={() => go(l.href)}
                  className="text-left text-[#121212]/80 font-medium py-1"
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={(e) => {
                  fireConfetti(e);
                  go("#contact");
                }}
                className="btn-gold rounded-full px-6 py-3 text-sm font-bold w-full"
                data-testid="mobile-book-date-btn"
              >
                Book Your Date
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
